import React, { useMemo, useState } from 'react';
import { AnnotatableTimeChart } from '@/components/report-validation/chart/AnnotatableTimeChart';
import { valueAt } from '@/components/report-validation/chart/chartMath';
import type {
  ChartSeries,
  ChartPhoto,
  ChartSegment,
  ChartKeypoint,
  Domain,
} from '@/components/report-validation/chart/types';

// Throwaway demo/harness for the shared annotatable time-chart (item E). Renders
// the chart standalone against mock data so it's reviewable before the section
// editors (F1/F2/F3) mount it. NOT a production route — orchestrator owns the
// real mount (Интерактив slot in ReportEditorV1); rename/remove when it lands.

const DAY_START = Date.UTC(2026, 5, 30, 5, 0, 0); // 08:00 МСК-ish, fixed (no Date.now → deterministic)
const DAY_END = DAY_START + 14 * 3600_000;
const FULL: Domain = [DAY_START, DAY_END];

// Deterministic pseudo-random so the demo looks the same every reload.
function rng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function buildSeries(): ChartSeries[] {
  const r = rng(42);
  const fill: { t: number; v: number }[] = [];
  const queue: { t: number; v: number }[] = [];
  const cashiers: { t: number; v: number }[] = [];
  const STEP = 15 * 60 * 1000;
  for (let t = DAY_START; t <= DAY_END; t += STEP) {
    const h = (t - DAY_START) / 3600_000;
    // fill% ramps up in the morning, dips midday, recovers, declines at close.
    const base = 30 + 55 * Math.sin((Math.PI * h) / 14) - (h > 12 ? (h - 12) * 8 : 0);
    fill.push({ t, v: Math.max(5, Math.min(100, base + (r() - 0.5) * 12)) });
    // queue: lunch + evening peaks.
    const q = 6 * Math.exp(-((h - 5) ** 2) / 2) + 5 * Math.exp(-((h - 11) ** 2) / 3);
    queue.push({ t, v: Math.max(0, Math.round(q + (r() - 0.5) * 2)) });
    // cashiers: 1 baseline, 2 during peaks.
    cashiers.push({ t, v: q > 3 ? 2 : 1 });
  }
  return [
    { id: 'fill', kind: 'area', points: fill, axis: 'left', color: '#10b981', label: 'Заполненность, %' },
    { id: 'queue', kind: 'bar', points: queue, axis: 'right', color: '#f59e0b', label: 'Очередь, чел', bucketMs: STEP },
    { id: 'cashiers', kind: 'step', points: cashiers, axis: 'right', color: '#6366f1', label: 'Кассы' },
  ];
}

// tiny inline SVG placeholder frames (no network → always renders on staging).
function placeholder(label: string, hue: number): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='120'><rect width='160' height='120' fill='hsl(${hue},40%,80%)'/><text x='80' y='64' font-family='sans-serif' font-size='14' fill='hsl(${hue},50%,30%)' text-anchor='middle'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function ChartDemoPage() {
  const series = useMemo(buildSeries, []);
  const photos: ChartPhoto[] = useMemo(() => {
    const out: ChartPhoto[] = [];
    for (let t = DAY_START; t <= DAY_END; t += 60 * 60 * 1000) {
      const clock = new Date(t).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      out.push({ t, url: placeholder(clock, ((t - DAY_START) / 3600_000) * 25), caption: clock });
    }
    return out;
  }, []);

  const [segments, setSegments] = useState<ChartSegment[]>([]);
  const [keypoints, setKeypoints] = useState<ChartKeypoint[]>([
    { id: 'kp-open', t: DAY_START + 30 * 60 * 1000 },
  ]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createKind, setCreateKind] = useState<'segment' | 'none'>('segment');

  const fillPts = series[0].points;
  const selSeg = segments.find((s) => s.id === selectedId);
  const selKp = keypoints.find((k) => k.id === selectedId);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div>
        <h1 className="text-lg font-semibold text-foreground">Annotatable chart — demo harness</h1>
        <p className="text-sm text-muted-foreground">
          Mock day (fill% area · queue bars · cashier steps). Annotate = drag to create a segment;
          tap a segment to select and drag its pins; drag the keypoint. Shift = temp-zoom, Ctrl =
          temp-pan; wheel/marquee zoom; double-click / «весь день» resets. Two-finger pinch on
          touch.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={createKind === 'none'}
          onChange={(e) => setCreateKind(e.target.checked ? 'none' : 'segment')}
        />
        Keypoint-only mode (F2 style — annotate drag disabled, keypoints only dragged)
      </label>

      <AnnotatableTimeChart
        series={series}
        photos={photos}
        domainFull={FULL}
        height={320}
        annotateCreates={createKind}
        segments={segments}
        keypoints={keypoints}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onSegmentCreate={(seg) => setSegments((prev) => [...prev, seg])}
        onSegmentChange={(seg) => setSegments((prev) => prev.map((s) => (s.id === seg.id ? seg : s)))}
        onKeypointChange={(kp) => setKeypoints((prev) => prev.map((k) => (k.id === kp.id ? kp : k)))}
        readout={
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
            {selSeg ? (
              <div className="space-y-1">
                <div className="font-medium text-foreground">Segment {selSeg.id}</div>
                <div className="text-muted-foreground">
                  {new Date(selSeg.t_start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  {' – '}
                  {new Date(selSeg.t_end).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  {/* E5: pre-computed value from underlying data (editor refines) */}
                  fill@start ≈ {Math.round(valueAt(fillPts, selSeg.t_start) ?? 0)}%
                </div>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-0.5 text-xs text-destructive"
                  onClick={() => {
                    setSegments((prev) => prev.filter((s) => s.id !== selSeg.id));
                    setSelectedId(null);
                  }}
                >
                  Удалить сегмент
                </button>
              </div>
            ) : selKp ? (
              <div className="space-y-1">
                <div className="font-medium text-foreground">Keypoint {selKp.id}</div>
                <div className="text-muted-foreground">
                  {new Date(selKp.t).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  {' · '}fill ≈ {Math.round(valueAt(fillPts, selKp.t) ?? 0)}%
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">Ничего не выбрано — нажмите на аннотацию.</span>
            )}
          </div>
        }
      />

      <div className="text-xs text-muted-foreground">
        segments: {segments.length} · keypoints: {keypoints.length}
      </div>
    </div>
  );
}
